import {ISelectItem} from '../../../shared/types';

export const FilterOptions: ISelectItem[] = [
  {key: 'DRAFT', value: 'Draft'},
  {key: 'INPROGRESS', value: 'In Progress'},
  {key: 'FAILED', value: 'Failed'},
  {key: 'COMPLETED', value: 'Completed'},
];

export const getSortOptions = (
  t: (key: string, options?: Record<string, unknown>) => string,
  isProduction: boolean
): ISelectItem[] => {
  if (isProduction) {
    return [
      {
        key: 'ProductionDate',
        value: t('productionsAndExports.sortOptions.production.date'),
      },
      {
        key: 'ProductionName',
        value: t('productionsAndExports.sortOptions.production.name'),
      },
      {
        key: 'UserName',
        value: t('productionsAndExports.sortOptions.production.user'),
      },
      {
        key: 'Status',
        value: t('productionsAndExports.sortOptions.production.status'),
      },
    ];
  }

  return [
    {
      key: 'ProductionDate',
      value: t('productionsAndExports.sortOptions.export.date'),
    },
    {
      key: 'Status',
      value: t('productionsAndExports.sortOptions.export.status'),
    },
    {
      key: 'UserName',
      value: t('productionsAndExports.sortOptions.export.user'),
    },
  ];
};

export const stripSetTypeSuffix = (label?: string | null): string => {
  if (!label) {
    return '';
  }

  return label
    .trim()
    .replace(/\((Production Set|Export Set)\)$/i, '')
    .trim();
};

export const SetDataOptions: ISelectItem[] = [
  {
    label: 'Production Set',
    value: 'ProductionSet',
  },
  {
    label: 'Export Set',
    value: 'ExportSet',
  },
];

export const formatProductionSetTimestamp = (isoString?: string): string => {
  if (!isoString) {
    return '';
  }

  const [datePart, timePartRaw] = isoString.split('T');
  if (!datePart || !timePartRaw) {
    return isoString;
  }

  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) {
    return isoString;
  }

  let timePart = timePartRaw;
  if (timePart.includes('.')) {
    timePart = timePart.split('.')[0];
  }
  timePart = timePart.replace('Z', '');

  const formattedDate = `${month}/${day}/${year.slice(-2)}`;

  return `${formattedDate} ${timePart}`;
};

/**
 * Formats production status text based on productionUIStatus and status field.
 * Removes existing status suffixes and adds the correct one based on status.
 *
 * Rules:
 * - INPROGRESS → "Step Name..."
 * - COMPLETED → "Step Name Complete"
 * - FAILED → "Step Name Failed"
 *
 * @param productionUIStatus - The UI status text (e.g., "Reserving Bates Range Complete")
 * @param status - The status code (e.g., "FAILED", "COMPLETED", "INPROGRESS")
 * @param productionStateCode - Fallback if productionUIStatus is not available
 * @returns Formatted status text with appropriate suffix
 */
export const formatProductionStatus = (
  productionUIStatus?: string | null,
  status?: string | null,
  productionStateCode?: string | null
): string => {
  // Use productionUIStatus if available, otherwise fallback to productionStateCode
  const baseStatus = productionUIStatus || productionStateCode || '';

  if (!baseStatus) {
    return '';
  }

  // Normalize status to uppercase for comparison
  const normalizedStatus = (status || '').toUpperCase().trim();

  // If status is COMPLETED or FAILED, return productionUIStatus as-is (no modifications)
  if (
    normalizedStatus === 'COMPLETED' ||
    normalizedStatus === 'COMPLETE' ||
    normalizedStatus === 'SUCCESS' ||
    normalizedStatus === 'FAILED' ||
    normalizedStatus === 'FAIL' ||
    normalizedStatus === 'ERROR'
  ) {
    return baseStatus;
  }

  // For INPROGRESS status, strip "In Progress" if present, then add "..."
  if (normalizedStatus === 'INPROGRESS' || normalizedStatus === 'IN_PROGRESS') {
    //If productionUIStatus is "Cancel in Progress"
    if (baseStatus.toLowerCase().startsWith('cancel')) {
      return baseStatus;
    }
    // If productionUIStatus has "Complete" or "Completed" as suffix, don't add three dots
    const completeRegex = /[ \t]{1,20}Completed?[ \t]{0,20}$/i;
    if (completeRegex.exec(baseStatus)) {
      return baseStatus;
    }
    // Strip "In Progress" ONLY if it's present in productionUIStatus
    const inProgressRegex = /\s{1,20}In\s{1,20}Progress$/i;
    let stepName = baseStatus;
    if (inProgressRegex.exec(baseStatus.trimEnd())) {
      stepName = baseStatus.trimEnd().replace(inProgressRegex, '').trim();
    }
    return `${stepName}...`;
  }

  return baseStatus;
};
